import { BreakpointObserver } from "@angular/cdk/layout";
import { Component, EventEmitter, inject, LOCALE_ID, Output } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { FloorplanUploadErrorModalComponent } from "@components/floorplan-upload-error-modal/floorplan-upload-error-modal.component";
import {
  FloorplanUploadProcessError,
  FloorplanUploadProcessorService,
} from "@components/floorplan-upload-overlay/floorplan-upload-processor.service";
import { UploadedFloorplan } from "@components/floorplan-upload-overlay/uploaded-floorplan";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { ModalService } from "@odx/angular/components/modal";
import { ToastHelper } from "@utils/toast-helper.service";
import {
  buildUploadErrorModalData,
  mapUploadError,
  UploadErrorContext,
  UploadErrorKind,
  UploadErrorMapperConfig,
  UploadErrorModalData,
} from "@utils/upload-error-mapper";
import { filter, map, Observable } from "rxjs";
import { Breakpoints } from "../../styles/breakpoints";

@Component({
  selector: "app-floorplan-upload-overlay",
  templateUrl: "./floorplan-upload-overlay.component.html",
  styleUrls: ["./floorplan-upload-overlay.component.scss"],
  imports: [UiKitModule],
  standalone: true,
})
export class FloorplanUploadOverlayComponent {
  private userService = inject(UserService);
  private toastHelper = inject(ToastHelper);
  private floorplanUploadProcessor = inject(FloorplanUploadProcessorService);
  private projectService = inject(ProjectService);
  private localeId = inject(LOCALE_ID);
  private modalService = inject(ModalService);
  private breakpointObserver = inject(BreakpointObserver);

  @Output() floorplan$ = new EventEmitter<UploadedFloorplan>();
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;
  protected isUploading = false;

  private isErrorModalOpen = false;
  isTablet$ = this.breakpointObserver.observe(Breakpoints.tablet).pipe(map((result) => result.matches));

  private readonly MAX_PDF_FILE_SIZE_BYTES = 25 * 1024 * 1024;
  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
  }

  onEmptySelected() {
    const date = new Date();
    const plan = new UploadedFloorplan(
      $localize`:@@floorplanUpload.emptyFloorplan.name:Leerer Grundrissplan - ${date.toLocaleDateString(this.localeId)}:date:, ${date.toLocaleDateString(this.localeId)}:time:`,
      undefined,
    );
    this.floorplan$.emit(plan);
  }

  async onFileSelected(event: any) {
    const files: File[] = Array.from(event.target.files ?? []);
    if (!files.length) {
      return;
    }

    if (this.isUploading) {
      return;
    }

    const uploadToastTitle = $localize`:@@toast.floorplanUpload.started.title:Grundrissplan Einrichtung`;
    const uploadToastMessage = $localize`:@@toast.floorplanUpload.started.message:Der Grundrissplan wird eingerichtet.`;
    const input = event.target as HTMLInputElement | null;
    const { errors, successCount } = await this.processUpload(files, uploadToastTitle, uploadToastMessage, input);

    if (successCount > 0) {
      this.toastHelper.success(
        this.getSuccessToastTitle(successCount),
        $localize`:@@toast.floorplanUpload.success.message:Der Grundrissplan ist jetzt verfügbar.`,
      );
    }

    if (errors.length > 0) {
      this.showUploadError(errors);
    }
  }

  private async processUpload(
    files: File[],
    uploadToastTitle: string,
    uploadToastMessage: string,
    input: HTMLInputElement | null,
  ) {
    this.toastHelper.message(uploadToastTitle, uploadToastMessage, true);
    this.isUploading = true;

    const errors: UploadErrorContext[] = [];
    let successCount = 0;

    try {
      const result = await this.floorplanUploadProcessor.processFiles(files, this.MAX_PDF_FILE_SIZE_BYTES);
      result.uploaded.forEach((plan) => this.floorplan$.emit(plan));
      successCount = result.uploaded.length;
      errors.push(...this.mapProcessErrors(result.errors));
    } finally {
      if (input) {
        input.value = "";
      }
      this.isUploading = false;
      this.toastHelper.dismiss(uploadToastTitle);
    }

    return { errors, successCount };
  }

  private mapProcessErrors(errors: FloorplanUploadProcessError[]) {
    return errors.map(({ file, error }) => mapUploadError(error, file.name));
  }

  private showUploadError(errors: UploadErrorContext[]) {
    const config = this.getUploadErrorConfig();
    const toastTitle = $localize`:@@toast.floorplanUpload.failed.title:Grundrissplan Upload fehlgeschlagen`;
    const toastMessage = $localize`:@@toast.floorplanUpload.failed.message:Der Grundrissplan-Upload ist fehlgeschlagen. Klicken Sie auf "Weitere Informationen" für mögliche Lösungen.`;
    const actionLabel = $localize`:@@toast.floorplanUpload.failed.action.moreInfo:Weitere Informationen`;
    const modalData: UploadErrorModalData = buildUploadErrorModalData(
      errors[0],
      errors.length,
      config,
      this.getErrorFileNames(errors, config),
    );

    this.toastHelper.error(toastTitle, toastMessage, true, [
      {
        label: actionLabel,
        action: async () => {
          if (this.isErrorModalOpen) {
            return;
          }
          this.isErrorModalOpen = true;
          try {
            const ref = this.modalService.open(FloorplanUploadErrorModalComponent, { data: modalData });
            ref.onClose$.subscribe({
              next: () => {
                this.isErrorModalOpen = false;
              },
              error: () => {
                this.isErrorModalOpen = false;
              },
              complete: () => {
                this.isErrorModalOpen = false;
              },
            });
          } catch {
            this.isErrorModalOpen = false;
          }
        },
      },
    ]);
  }

  private getErrorFileNames(errors: UploadErrorContext[], config: UploadErrorMapperConfig) {
    const names = errors.map((error) => error.fileName ?? config.unknownFileName);
    return names.filter((name) => name.length > 0);
  }

  private getSuccessToastTitle(successCount: number) {
    if (successCount === 1) {
      return $localize`:@@toast.floorplanUpload.success.title.single:1 Grundrissplan hinzugefügt`;
    }
    return $localize`:@@toast.floorplanUpload.success.title.multi:${successCount}:count: Grundrisspläne hinzugefügt`;
  }

  private getUploadErrorConfig(): UploadErrorMapperConfig {
    return {
      localeId: this.localeId,
      maxSizeBytes: this.MAX_PDF_FILE_SIZE_BYTES,
      unknownFileName: $localize`:@@floorplanUploadError.file.unknown:die Datei`,
      solutionPrefix: "floorplanUploadError.solution.fallback.",
      fallbackSolutions: [
        $localize`:@@floorplanUploadError.solution.fallback.1:Datei überprüfen und erneut hochladen.`,
        $localize`:@@floorplanUploadError.solution.fallback.2:Dateigröße reduzieren oder anderes Format verwenden.`,
        $localize`:@@floorplanUploadError.solution.fallback.3:Bei wiederholtem Fehler Support kontaktieren.`,
      ],
      modalText: {
        title: $localize`:@@modal.floorplanUploadError.title:Grundrissplan Upload fehlgeschlagen`,
        descriptionSingle: (fileName: string) =>
          $localize`:@@modal.floorplanUploadError.description.single:Der Grundrissplan ${fileName}:fileName: konnte nicht hochgeladen werden.`,
        descriptionMultiple: (count: number) =>
          $localize`:@@modal.floorplanUploadError.description.multiple:${count}:count: Grundrisspläne konnten nicht hochgeladen werden.`,
        fileListTitle: $localize`:@@modal.floorplanUploadError.fileListTitle:Betroffene Dateien`,
        reasonMultiple: $localize`:@@modal.floorplanUploadError.reason.multiple:Grundrissplan konnte nicht verarbeitet werden.`,
        reasonTitle: $localize`:@@modal.floorplanUploadError.reasonTitle:Fehlergrund`,
        solutionTitle: $localize`:@@modal.floorplanUploadError.solutionTitle:Lösungsvorschläge`,
        closeLabel: $localize`:@@modal.floorplanUploadError.button.close:Schließen`,
      },
      reasonByKind: {
        [UploadErrorKind.OFFLINE]: () =>
          $localize`:@@floorplanUploadError.reason.offline:Der Upload benötigt eine aktive Internetverbindung.`,
        [UploadErrorKind.PDF_TOO_LARGE]: ({ maxSizeMb }) =>
          $localize`:@@floorplanUploadError.reason.pdfTooLarge:Die PDF-Datei überschreitet die maximale Größe von ${maxSizeMb}:size: MB.`,
        [UploadErrorKind.UNSUPPORTED_TYPE]: () =>
          $localize`:@@floorplanUploadError.reason.unknown:Beim Hochladen ist ein unerwarteter Fehler aufgetreten.`,
        [UploadErrorKind.CONVERSION_FAILED]: () =>
          $localize`:@@floorplanUploadError.reason.conversionFailed:Der Grundriss konnte nicht verarbeitet werden.`,
        [UploadErrorKind.INVALID_IMAGE]: () =>
          $localize`:@@floorplanUploadError.reason.invalidImage:Das Bild konnte nicht verarbeitet werden.`,
        [UploadErrorKind.INVALID_PDF]: () =>
          $localize`:@@floorplanUploadError.reason.invalidPdf:Das PDF konnte nicht verarbeitet werden.`,
        [UploadErrorKind.UNKNOWN]: () =>
          $localize`:@@floorplanUploadError.reason.unknown:Beim Hochladen ist ein unerwarteter Fehler aufgetreten.`,
      },
    };
  }
}
